import { marked } from 'marked';
import DOMPurify from 'dompurify';
import stockData from '../data/stock_symbols.json';

// ── Stock symbols database (loaded synchronously via bundler) ──
// No async fetch = no race condition = no window where fake tickers are accepted
const _stockSymbols = stockData.symbols;

/**
 * Check if a symbol is a valid stock ticker.
 * Returns { valid, name, exchange } or { valid: false }.
 * Symbols are loaded synchronously — always available, zero race conditions.
 */
export function isValidTicker(symbol) {
    const entry = _stockSymbols[symbol.toUpperCase()];
    if (entry) return { valid: true, name: entry.name, exchange: entry.exchange };
    return { valid: false };
}

/**
 * Search stock symbols for autocomplete.
 * Matches by symbol prefix or company name substring.
 * Returns array of { symbol, name, exchange }.
 */
export function searchStockSymbols(query, limit = 8) {
    if (!_stockSymbols || !query) return [];
    const q = query.toUpperCase();
    const ql = query.toLowerCase();
    const results = [];

    for (const [symbol, data] of Object.entries(_stockSymbols)) {
        if (symbol.startsWith(q) || data.name.toLowerCase().includes(ql)) {
            results.push({ symbol, name: data.name, exchange: data.exchange });
            if (results.length >= limit) break;
        }
    }

    // Sort: exact prefix matches first, then by symbol length
    results.sort((a, b) => {
        const aStartsWith = a.symbol.startsWith(q);
        const bStartsWith = b.symbol.startsWith(q);
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        return a.symbol.length - b.symbol.length;
    });

    return results;
}

// ── Marked renderer configuration ──
const renderer = new marked.Renderer();
renderer.heading = ({ text }) => `<strong>${text}</strong> `;
renderer.image = () => '';
renderer.html = () => '';
// Custom link renderer — add post-link class and open in new tab
renderer.link = ({ href, text }) => {
    if (href && !/^https?:\/\//i.test(href)) {
        return escapeHtml(text || href);
    }
    const safeHref = escapeHtml(href || '');
    const isShortUrl = href && href.includes('/s/');
    const isCashtag = href && href.includes('google.com/finance/quote/');
    const cssClass = isCashtag
        ? 'post-link post-link--cashtag'
        : isShortUrl
            ? 'post-link post-link--short'
            : 'post-link';
    const displayText = escapeHtml(text || href || '');
    return `<a href="${safeHref}" class="${cssClass}" target="_blank" rel="noopener noreferrer">${displayText}</a>`;
};

function escapeHtml(str) {
    if (!str) return '';
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

marked.setOptions({
    renderer,
    breaks: true,
    gfm: true,
    headerIds: false,
    mangle: false,
});

const PURIFY_CONFIG = {
    ALLOWED_TAGS: ['p', 'strong', 'em', 'del', 'code', 'blockquote', 'br', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    KEEP_CONTENT: true,
    ADD_ATTR: ['target'],
};

DOMPurify.addHook('afterSanitizeAttributes', function (node) {
    if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
        const href = node.getAttribute('href') || '';
        if (!/^https?:\/\//i.test(href)) {
            node.removeAttribute('href');
        }
    }
});

/**
 * Render a post's details text as formatted HTML.
 * Validates cashtags against the stock symbols database.
 */
export function renderMarkdown(text) {
    if (!text) return '';

    // 1. Convert VALIDATED cashtags ($AAPL) to links
    const cashtagPattern = /(?<![a-zA-Z0-9\/])\$([A-Z]{1,5})(?![a-zA-Z])/g;
    let processed = text.replace(cashtagPattern, (match, symbol) => {
        const check = isValidTicker(symbol);
        if (!check.valid) return match; // Not a real ticker — leave as plain text
        const exchange = check.exchange === 'CRYPTO' ? '' : `:${check.exchange}`;
        return `[$${symbol}](https://www.google.com/finance/quote/${symbol}${exchange})`;
    });

    // 2. Auto-linkify plain URLs
    const urlPattern = /(?<!\[.*?\]\()(?<!\()(https?:\/\/[^\s<>\])"]+)/g;
    processed = processed.replace(urlPattern, (url) => {
        return `[${url}](${url})`;
    });

    const html = marked.parse(processed);
    return DOMPurify.sanitize(html, PURIFY_CONFIG);
}

export function markdownProps(text) {
    return {
        dangerouslySetInnerHTML: { __html: renderMarkdown(text) },
    };
}
